import { createContext, useCallback, useReducer, useContext, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { getActiveMainContracts } from 'utils/contracts'

import { ApplicationContext } from 'context/Application'
import { useProfile } from 'context/Profile'

import { useQuery } from 'requests'
import { GET_ORGANIZATION_APPLICATION } from './query'

import { LoadingDots } from '@nfinite/react-hubstairs/components'
import { OrganizationPickerModal, OrganizationPickerModalBlocking } from 'components/OrganizationPicker'

export const OrganizationContext = createContext()
const OrganizationDispatchContext = createContext()

const SET_OPEN_ORGANIZATION_PICKER = 'set open organization picker'

function useGetOrganizationDetails({ organizationId, isAuthenticated }) {
  const { data, loading, called, refetch } = useQuery(GET_ORGANIZATION_APPLICATION, {
    skip: !(isAuthenticated && organizationId),
    variables: {
      organizationId,
      contractFilter: getActiveMainContracts(),
    },
  })

  const organization = data?.organization

  if (!organizationId || (called && !loading)) {
    return { organization, refetchOrganization: refetch }
  }
  // Implicit return as null to represent that organization is still being fetched (ongoing useQuery)
  // so it can later fallback on display of LoadingDots mid page refresh/query running
  // on the OrganizationBlocker
  return { organization: null, refetchOrganization: refetch }
}

function init() {
  return { pickerOpen: false }
}

function reducer(state, action) {
  const { type, payload } = action

  switch (type) {
    case SET_OPEN_ORGANIZATION_PICKER: {
      return { ...state, pickerOpen: payload }
    }
    default:
      throw new Error()
  }
}

function OrganizationFetcher({ organizationId, isAuthenticated, children }) {
  const { organization, refetchOrganization } = useGetOrganizationDetails({ organizationId, isAuthenticated })
  return children({ organization, refetchOrganization })
}

function OrganizationProvider({ children }) {
  const { isAuthenticated } = useProfile()
  const [state, dispatch] = useReducer(reducer, init())

  const { organization: organizationId } = useContext(ApplicationContext)

  return (
    <OrganizationFetcher key={organizationId} organizationId={organizationId} isAuthenticated={isAuthenticated}>
      {({ organization, refetchOrganization }) => (
        <OrganizationContext.Provider value={{ ...state, organization, refetchOrganization }}>
          <OrganizationDispatchContext.Provider value={dispatch}>
            {isAuthenticated ? <OrganizationBlocker>{children}</OrganizationBlocker> : children}
            {isAuthenticated && <OrganizationPicker />}
            {isAuthenticated && <OrganizationZombieUrl />}
          </OrganizationDispatchContext.Provider>
        </OrganizationContext.Provider>
      )}
    </OrganizationFetcher>
  )
}

function OrganizationPicker() {
  const { pickerOpen, setOpenOrganizationPicker } = useApplicationOrganizationPicker()

  const onClose = useCallback(() => {
    setOpenOrganizationPicker(false)
  }, [setOpenOrganizationPicker])

  return <OrganizationPickerModal id="menu-organization-picker" open={pickerOpen} onClose={onClose} />
}

/**
 * Ensure that application doesnt render in authenticated mode for users organization until organization is set
 * in Application provider and organization details are fetched
 *
 * NB: It is not possible to render the application in authenticated mode for an organization user, without having an
 * organization selected because of 2 issues:
 * - Queries and mutations must go with a header hub-organization (eg: updateMe)
 * - Permission check is required on nav bar items: without an organization selected, which permissionContext do we use ?
 */
function OrganizationBlocker({ children }) {
  const { activeOrganizations } = useProfile()
  const { organization } = useApplicationOrganization()
  const { organization: organizationId } = useContext(ApplicationContext)

  // cover delay between authentication and fetching organization details
  // note that it is an implicit check for undefined, so organization === null
  // fallbacks to later display of LoadingDots
  if (activeOrganizations?.length === 1 && organization === undefined) {
    return <LoadingDots position="absolute" />
  }
  // if organization is undefined, force selection of organization
  // note that it is an implicit check for undefined, so organization === null fallbacks
  // to later display of LoadingDots
  else if (organization === undefined) {
    return <OrganizationPickerModalBlocking id="blocking-organization-picker" />
  }
  // Dont render application when switching organization
  else if (organizationId && organization?.id !== organizationId) {
    return <LoadingDots position="absolute" />
  }

  return children
}

function OrganizationZombieUrl() {
  const { organization: organizationId } = useContext(ApplicationContext)
  const { key } = useLocation()

  useEffect(() => {
    const url = new URL(window.location)
    const { pathname, searchParams } = url
    searchParams.set('organizationId', organizationId)
    window.history.replaceState({}, null, `${pathname}?${searchParams.toString()}`)
  }, [key, organizationId])

  return null
}

function useOrganizationState() {
  const { pickerOpen, organization, refetchOrganization } = useContext(OrganizationContext)
  return { pickerOpen, organization, refetchOrganization }
}

function useOrganizationDispatch() {
  const dispatch = useContext(OrganizationDispatchContext)

  const setOpenOrganizationPicker = useCallback(
    open => {
      dispatch({ type: SET_OPEN_ORGANIZATION_PICKER, payload: open })
    },
    [dispatch],
  )

  return { setOpenOrganizationPicker }
}

/**
 * a super user only has access to organization contracts on /me
 */
function combineOrganizationData({ organization, activeOrganizations }) {
  if (!organization) {
    // To have implicit return of null or undefined accordingly
    return organization
  }

  const userOrganization = activeOrganizations?.find(({ organization: o }) => o.id === organization.id)?.organization

  const contractsUserOrganization = userOrganization?.paginatedContracts.items
  const contractsOrganization = organization.paginatedContracts?.items

  const combined = {
    ...organization,
    contracts: contractsOrganization ?? contractsUserOrganization,
  }

  delete combined.paginatedContracts

  return combined
}

function useApplicationOrganization() {
  const { organization, refetchOrganization } = useOrganizationState()
  const { activeOrganizations } = useProfile()

  return { organization: combineOrganizationData({ organization, activeOrganizations }), refetchOrganization }
}

function useApplicationOrganizationPicker() {
  const { pickerOpen } = useOrganizationState()
  const { setOpenOrganizationPicker } = useOrganizationDispatch()

  return {
    pickerOpen,
    setOpenOrganizationPicker,
  }
}

function useContractType({ activeOrganizations }) {
  const organizationContext = useContext(OrganizationContext)

  const organization = useMemo(
    () =>
      organizationContext?.organization
        ? combineOrganizationData({ organization: organizationContext?.organization, activeOrganizations })
        : null,

    [organizationContext?.organization, activeOrganizations],
  )

  const contractType = organization?.contracts?.map(contract => contract.contractType)

  return contractType || []
}

export { OrganizationProvider, useApplicationOrganization, useApplicationOrganizationPicker, useContractType }
